import * as React from 'react';
import styled from 'styled-components';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { withRouter } from 'react-router';
import { Switch } from 'react-router-dom';
import { push as Push } from 'redux-first-history';
import { AccessLevel, ProductType } from '@dev/ee-api/ee-api-models';
import {
    ArrowDropDownIcon,
    Container,
    palette,
    PrimaryButton,
    SecondaryButton,
    SimplePopover,
    TabsPanel,
} from '@dev/ee-components';
import { findIndex } from 'lodash-es';
import { capitalizeString, generateIdBasedOnPath } from '@dev/ee-tools';

import LockSvg from '@/services/icons/lock.svg';
import PrivateRoute from '@/services/Router/PrivateRoute';
import { ApplicationState as State } from '@/store';
import { SetPickedContactEmail } from '@/store/contacts/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { useSecurity } from '@/services/hooks/useSecurity';
import { useUpselling } from '@/services/hooks/useUpselling';
import { getGeneralInfo } from '@/store/account/actions';

import SingleContactOverlay from '../SingleContactOverlay';
import ContactsHistory from './ContactsHistory';
import DeviceDetailsOverview from './DeviceDetails';
import GeographyOverview from './Geography';
import Lists from './Lists';
import ContactOverview from './Overview';
import Segments from './Segments';
import UnsubscribedOverview from './Unsubscribed';
import { ContactsProps, DispatchProps, Props } from './ContactsInterface';

// declare const ee: any;

const dispatchProps = {
    Push,
    SetPickedContactEmail,
};

const ArrowDropdownContainer = styled.div`
    svg {
        margin-bottom: -4px;
    }
`;

const TopMenuButtons = styled.div`
    button + button {
        margin-left: 16px;
    }
`;

const mapState = (state: State, ownProps: ContactsProps): ContactsProps => ({
    ...ownProps,
    account: state.account,
    browser: state.browser,
    contacts: state.contacts,
    config: state.config,
});

const LockIcon = styled(LockSvg as any)`
    margin-right: 12px;
`;

const CustomFieldsButton = () => {
    const { AccountAccessLevel } = useSelector(getGeneralInfo);
    const hasRequiredAccessLevel = AccountAccessLevel.includes(AccessLevel.ViewCustomFields);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isUpsellingVisible } = useUpselling(AccessLevel.ViewCustomFields);
    const { hasLegacyPlan } = useSecurity();

    if (hasLegacyPlan && !hasRequiredAccessLevel) {
        return null;
    }

    return (
        <SecondaryButton
            id={generateIdBasedOnPath('custom-fields-button')}
            text={
                <>
                    {isUpsellingVisible && <LockIcon />}
                    {t('customfields')}
                </>
            }
            onClick={(): void => {
                dispatch(Push(ContactsPaths.CustomFields));
            }}
        />
    );
};

class Contacts extends React.Component<Props> {
    private enableContactFeatures(): boolean {
        const { account } = this.props;
        return account.GeneralInfo?.EnableContactFeatures;
    }

    private isDeviceDetailsTabVisible(): boolean {
        const { account } = this.props;
        return [ProductType.Marketing, ProductType.CreatorSuite].includes(account?.GeneralInfo?.ProductType);
    }

    /**
     * Render tabs on top of the all main Contacts screens. Content depends on Access Levels
     */
    private renderContactsTabs = (): JSX.Element => {
        const activeTab = this.getActiveTabFromURL();
        const { t, Push, browser, config } = this.props;
        const isMobile = browser.lessThan.hd;
        const contactTabs = [
            {
                label: t('overview'),
                id: 'overview-tab',
            },
            {
                label: t('segments'),
                id: 'segments-tab',
            },
        ];

        if (this.enableContactFeatures()) {
            contactTabs.push({
                label: t('lists'),
                id: 'lists-tab',
            });
        }

        if (this.enableContactFeatures() && !isMobile) {
            contactTabs.push({
                label: t('history'),
                id: 'history-tab',
            });
        }

        contactTabs.push({
            label: t('unsubscribedtab'),
            id: 'unsubscribed-tab',
        });

        if (this.isDeviceDetailsTabVisible()) {
            contactTabs.push({
                label: capitalizeString(t('devicedetailstab')),
                id: 'devicedetails-tab',
            });
        }
        contactTabs.push({
            label: capitalizeString(t('geography')),
            id: 'geography-tab',
        });

        const additionalContent = isMobile ? this.renderMobileAdditionalContent() : this.renderAdditionalContent();

        return (
            <TabsPanel
                title={t('contacts')}
                additionalHeaderContent={additionalContent}
                tabBackground={isMobile || config?.isStandalone ? '' : palette.basic.paper}
                isMobile={isMobile}
                value={activeTab}
                onChange={(_e, newTabValue): void => {
                    Push(this.changeTab(newTabValue));
                }}
                tabs={contactTabs}
            />
        );
    };

    /**
     * Check match.path to recognize selected tab
     */
    private getActiveTabFromURL = (): number => {
        const { browser } = this.props;
        const isMobile = browser.lessThan.hd;
        const TabsPanelURL = [
            ContactsPaths.ContactsOverview,
            ContactsPaths.Segments,
            ...(this.enableContactFeatures() ? [ContactsPaths.Lists] : []),
            ...(this.enableContactFeatures() && !isMobile ? [ContactsPaths.ContactsHistory] : []),
            ContactsPaths.Unsubscribed,
            ContactsPaths.DeviceDetails,
            ContactsPaths.Geography,
        ];
        const { match } = this.props;
        const foundURL = findIndex(TabsPanelURL, (item): boolean => item === match.path);
        return foundURL >= 0 ? foundURL : 0;
    };

    /**
     * Prepare additional content for tabs (buttons and actions), depending on tabs selected
     */
    private renderAdditionalContent = (): JSX.Element => {
        const { match, t, Push, account } = this.props;
        const canModifyContacts = account?.GeneralInfo?.AccountAccessLevel.includes(AccessLevel.ModifyContacts);
        const addContactsData = [
            {
                label: t('uploadcontacts'),
                value: 'upload',
                id: generateIdBasedOnPath('dropdown-upload-contacts'),
            },
            {
                label: t('addcontact'),
                value: 'add',
                id: generateIdBasedOnPath('dropdown-add-contact'),
            },
        ];
        switch (match.path) {
            case ContactsPaths.ContactsHistory:
            case ContactsPaths.Unsubscribed:
            case ContactsPaths.DeviceDetails:
                break;
            case ContactsPaths.Lists:
                return (
                    <TopMenuButtons>
                        {canModifyContacts && (
                            <PrimaryButton
                                id={generateIdBasedOnPath('create-list-button')}
                                text={this.props.t('createlist')}
                                onClick={(): void => {
                                    Push(ContactsPaths.ListCreator);
                                }}
                            />
                        )}
                    </TopMenuButtons>
                );
            case ContactsPaths.Segments:
                return (
                    this.enableContactFeatures() && (
                        <TopMenuButtons>
                            <CustomFieldsButton />
                            {canModifyContacts && (
                                <PrimaryButton
                                    id={generateIdBasedOnPath('create-segment-button')}
                                    text={this.props.t('createsegment')}
                                    onClick={(): void => {
                                        Push(ContactsPaths.SegmentCreator);
                                    }}
                                />
                            )}
                        </TopMenuButtons>
                    )
                );
            case ContactsPaths.Geography:
                return null;
            case ContactsPaths.ContactsOverview:
            default:
                return (
                    canModifyContacts && (
                        <SimplePopover
                            id={generateIdBasedOnPath('add-contacts-dropdown')}
                            type="custom"
                            onItemClick={(_e: any, obj: { value: any }): void => {
                                Push(obj.value === 'add' ? ContactsPaths.ContactsAdd : ContactsPaths.ContactsUpload);
                            }}
                            options={addContactsData}
                            placement={'bottom-end'}
                        >
                            <PrimaryButton
                                id="add-contacts-button"
                                endIcon={
                                    <ArrowDropdownContainer>
                                        <ArrowDropDownIcon color="white" />
                                    </ArrowDropdownContainer>
                                }
                                text={t('addcontactsbtn')}
                            />
                        </SimplePopover>
                    )
                );
        }
    };

    private renderMobileAdditionalContent = (): JSX.Element => {
        const { match, t, account, Push } = this.props;
        const canModifyContacts = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyContacts);

        switch (match.path) {
            case ContactsPaths.ContactsHistory:
            case ContactsPaths.Unsubscribed:
                break;
            case ContactsPaths.Segments:
            case ContactsPaths.ContactsOverview:
            case ContactsPaths.Lists:
            default:
                return (
                    canModifyContacts && (
                        <PrimaryButton
                            id="add-contacts-button"
                            onClick={(): void => {
                                Push(ContactsPaths.ContactsAdd);
                            }}
                            text={t('addcontactbtn')}
                        />
                    )
                );
        }
    };

    /**
     * Prepare additional content for tabs (buttons and actions), depending on tabs selected
     * @param tabindex - numeric index of tab, taken from the clicked tab
     */
    private changeTab = (tabindex): string => {
        const { browser } = this.props;
        const isMobile = browser.lessThan.hd;

        const TabsPanelURL = [
            ContactsPaths.ContactsOverview,
            ContactsPaths.Segments,
            ...(this.enableContactFeatures() ? [ContactsPaths.Lists] : []),
            ...(this.enableContactFeatures() && !isMobile ? [ContactsPaths.ContactsHistory] : []),
            ContactsPaths.Unsubscribed,
            ...((this.isDeviceDetailsTabVisible() && [ContactsPaths.DeviceDetails]) || []),
            ContactsPaths.Geography,
        ];

        return TabsPanelURL[tabindex];
    };

    public render(): JSX.Element {
        const contactEmail = this.props.contacts?.unsubscribedContacts?.pickedContactEmail;
        const resetContactEmail = (): void => {
            this.props.SetPickedContactEmail(null);
        };
        return (
            <Container variant="full">
                {this.renderContactsTabs()}
                <Switch>
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={ContactsHistory}
                        path={ContactsPaths.ContactsHistory}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={Segments}
                        path={ContactsPaths.Segments}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={Lists}
                        path={ContactsPaths.Lists}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={ContactOverview}
                        path={ContactsPaths.ContactsOverview}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={UnsubscribedOverview}
                        path={ContactsPaths.Unsubscribed}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={DeviceDetailsOverview}
                        path={ContactsPaths.DeviceDetails}
                        disableBanners
                        preventProductSwitching
                    />
                    <PrivateRoute
                        exact
                        accessLevel={[AccessLevel.ViewContacts]}
                        component={GeographyOverview}
                        path={ContactsPaths.Geography}
                        disableBanners
                        preventProductSwitching
                    />
                </Switch>

                {this.props.contacts?.unsubscribedContacts?.pickedContactEmail && (
                    <SingleContactOverlay email={contactEmail} action={resetContactEmail} />
                )}
            </Container>
        );
    }
}

export default compose(
    withRouter,
    withTranslation(),
    connect<ContactsProps, DispatchProps>(mapState, dispatchProps)
)(Contacts);
